import { mdiClockAlert } from '@mdi/js';
import { Currency } from 'common/classes/currency';
import { DangerButton } from 'common/components/buttons';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import { DEFAULT_PAGE_SIZE } from 'common/components/table/config';
import { TableTitle } from 'common/components/table/title';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import {
  ClientTransferRequestsActions,
  ClientTransferRequest,
  ClientTransferRequestStatuses,
} from 'common/state/banking/transfers/actions';
import { clientTransferRequestsRecipe } from 'common/state/banking/transfers/recipes';
import {
  ClientTransferRequestsState,
  defaultClientTransferRequestsState,
} from 'common/state/banking/transfers/states';
import { doGetClientTransferRequests } from 'common/state/banking/transfers/thunkActions';
import React, { useEffect, useMemo, useState } from 'react';
import { ModalMode } from '../customerBanking';

interface WithdrawalsTableProps {
  setSelectedRow: (transferRequest: ClientTransferRequest) => void;
  setModalMode: (mode: ModalMode) => void;
}

export const WithdrawalsTable = ({
  setSelectedRow,
  setModalMode,
}: WithdrawalsTableProps) => {
  const [state, dispatch] = useProducerWithThunks<
    ClientTransferRequestsState,
    ClientTransferRequestsActions
  >(clientTransferRequestsRecipe, defaultClientTransferRequestsState);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const columns = useMemo(
    (): ColumnProp<ClientTransferRequest>[] => [
      {
        key: 'date',
        title: 'Date',
        valueFormatter: (row) => new Date(row.date).toLocaleString(),
      },
      {
        key: 'requester',
        title: 'Requester',
        valueFormatter: (row) => row.requester.split('/').slice(-2, -1)[0],
      },
      {
        key: 'receiver',
        title: 'Receiver',
        valueFormatter: (row) => row.receiver.split('/').slice(-2, -1)[0],
      },
      {
        key: 'sender',
        title: 'Sender',
        valueFormatter: (row) => row.sender.split('/').slice(-2, -1)[0],
      },
      { key: 'description', title: 'Description' },
      { key: 'reference', title: 'Reference' },
      { key: 'currency', title: 'Currency' },
      {
        key: 'amount',
        title: 'Amount',
        valueFormatter: (row) =>
          new Currency(row.amount, row.currency).toElement({ colour: false }),
        csvFormatter: (row) =>
          new Currency(row.amount, row.currency).toString({ symbol: false }),
      },
      { key: 'status', title: 'Status' },
      {
        key: 'cancel',
        title: 'Cancel',
        valueFormatter: (row) => (
          <DangerButton
            disabled={row.status !== ClientTransferRequestStatuses.OPEN}
            onClick={() => {
              setSelectedRow(row);
              setModalMode('withdrawalCancel');
            }}
          >
            Cancel
          </DangerButton>
        ),
        isSortable: false,
      },
    ],
    [setSelectedRow, setModalMode]
  );

  useEffect(() => {
    dispatch(
      doGetClientTransferRequests({
        limit: pageSize,
        offset: pageSize * (pageNo - 1),
      })
    );
  }, [dispatch, pageNo, pageSize]);

  return (
    <>
      <TableTitle icon={mdiClockAlert} title="Withdrawal Requests" />
      <BaseTable
        columns={columns}
        rows={state.requests || []}
        csvName="Withdrawals"
        pagination={{
          count: state.count,
          pageNo,
          pageSize,
          setPageNo,
          setPageSize,
        }}
        isLoading={state.pending}
        error={state.error}
        isSortable={false}
      />
    </>
  );
};
